import {capitalize, Grid, Stack, Typography, IconButton} from "@mui/material";
import {targets, useReferentialContext} from "../../Context";
import FilterSet from "../../components/selectors/Filterset";
import React, {SetStateAction, useEffect, useState} from "react";
import dayjs from "dayjs";
import {NeonCard, NeonCardContent, NeonCardHeader} from "../../NeonComponents/NeonCards";
import AgentIcon from "../../components/Icons/AgentIcon";
import {Title} from "../../NeonComponents/Texts";
import formatters from "../../NeonComponents/Formatters";
import {scrollable} from "../../theme";
import WeaponIcon from "../../components/Icons/WeaponIcon";
import Box from "@mui/material/Box";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {NeonGlassItem} from "../../NeonComponents/NeonGlassItem";
import {StatsBlock} from "../../NeonComponents/NeonStatsBlock";


export default function CompetitivePlayerStats(){
    const ctx = useReferentialContext();
    const [player, setPlayer] = useState(ctx.selectedPlayer);
    const [from, setFrom] = useState(dayjs('2026-01-01'));
    const [until, setUntil] = useState(dayjs());

    const [agent, setAgent] = useState(undefined as string|undefined);
    const [map, setMap] = useState(undefined as string|undefined);
    const [side, setSide] = useState(undefined as string|undefined);
    const [economy, setEconomy] = useState(undefined as string|undefined);
    const [weapon, setWeapon] = useState(undefined as string|undefined);

    // Sync player from context when it becomes available
    useEffect(() => {
        if (!player && ctx.selectedPlayer) {
            setPlayer(ctx.selectedPlayer);
        }
    }, [ctx.selectedPlayer, player]);

    // Compute paramObject helper
    const computeParamObject = () => {
        if (!player) return null;
        
        const watermark = player.id + from.toISOString() + until.toISOString() + agent + map + side + economy + weapon;
        
        return {
            player: player.id,
            main_weapon: weapon,
            side: side,
            round_eco_type_no_bonus: economy,
            map: map,
            agent: agent,
            additional_filters: {
                date__gte: from.toISOString(),
                date__lt: until.toISOString(),
            },
            watermark
        };
    };

    const [paramObject, setParamObject] = useState<any>(() => computeParamObject() || {});

    // Update paramObject when any filter changes
    useEffect(() => {
        const newParamObject = computeParamObject();
        if (newParamObject) {
            setParamObject(newParamObject);
        }
    }, [player, from, until, agent, map, side, economy, weapon]);

    if (!player) {
        return <Grid container spacing={1} sx={{padding:'10px', height:'94%'}}>Loading...</Grid>;
    }

    return <Grid container spacing={1} sx={{padding:'10px', height:'94%'}}>
        <Grid size={12}>
        <FilterSet paramObject={paramObject} setParamObject={setParamObject} target={targets.competitive} player={player} setPlayer={setPlayer}
        date__gte={from} setDateGte={setFrom} date__lt={until} setDateLt={setUntil}/>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            {paramObject.player && <StatsByAgent paramObject={paramObject} agent={agent} setAgent={setAgent}/>}
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            {paramObject.player && <StatsByMap paramObject={paramObject} map={map} setMap={setMap}/>}
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
                {paramObject.player && <StatsBySide paramObject={paramObject} side={side} setSide={setSide}/>}
                {paramObject.player && <StatsByEcoType paramObject={paramObject} economy={economy} setEconomy={setEconomy}/>}
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            {paramObject.player && <StatsByWeapon paramObject={paramObject} weapon={weapon} setWeapon={setWeapon}/>}
        </Grid>
    </Grid>
}

interface StatsRowProps {
  stats: any;
  title?: React.ReactNode;
  decoration?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}

const StatsRow: React.FC<StatsRowProps> = React.memo(({ stats, title, decoration, selected, onClick }) => {
    return <Box sx={{width:'100%',alignItems:'center', justifyContent:'center', display:'flex'}}>
        <NeonGlassItem 
            isClickable 
            onClick={onClick}
            sx={{
                width:'90%',
                minHeight: '120px',
                position: 'relative',
                "&::before": selected
                    ? {
                        content: "''",
                        position: "absolute",
                        top: "15%",
                        bottom: "15%",
                        left: "0px",
                        width: "1px",
                        height: "70%",
                        background: "#ffffff",
                        boxShadow: `0px 0px 5px #fff, 0px 0px 20px #fff`,
                      }
                    : {},
            }}
        >
            <Grid container columns={4} spacing={1}>
                <Grid size={.5} sx={{alignItems:'center', justifyContent:'center', display:'flex'}}>
                    {title}
                </Grid>
                <Grid size={3.5} sx={{alignItems:'center', justifyContent:'center', display:'flex'}}>
                    {decoration}
                </Grid>
                <Grid size={1}><StatsBlock label={'Rounds'} value={stats.rounds} metricCode="rounds" formatter={formatters.integer}/></Grid>
                <Grid size={1}><StatsBlock label={'RWin%'} value={stats.win__avg} metricCode="win_avg" formatter={formatters.percent}/></Grid>
                <Grid size={1}><StatsBlock label={'KD'} value={stats.deaths_total ? stats.kills_total / stats.deaths_total : 0} metricCode="kd" formatter={formatters.float2}/></Grid>
                <Grid size={1}><StatsBlock label={'Kast'} value={stats.kast} metricCode="kast" formatter={formatters.percent}/></Grid>
                <Grid size={1}><StatsBlock label={'KPR'} value={stats.kpr} metricCode="kpr" formatter={formatters.float2}/></Grid>
                <Grid size={1}><StatsBlock label={'ADR'} value={stats.adr} metricCode="adr" formatter={formatters.float1}/></Grid>
                <Grid size={1}><StatsBlock label={'DPR'} value={stats.dpr} metricCode="dpr" formatter={formatters.float2}/></Grid>
                <Grid size={1}><StatsBlock label={'ACS'} value={stats.acs} metricCode="acs" formatter={formatters.float1}/></Grid>
                <Grid size={1}><StatsBlock label={'FB%'} value={stats.rounds ? stats.first_blood_total / stats.rounds : 0} metricCode="fb_pct" formatter={formatters.percent}/></Grid>
                <Grid size={1}><StatsBlock label={'FD%'} value={stats.rounds ? stats.first_deaths_total / stats.rounds : 0} metricCode="fd_pct" formatter={formatters.percent}/></Grid>
            </Grid>
        </NeonGlassItem>
    </Box>
});

// Carousel navigation component for paginated stats
interface CarouselNavProps {
    currentIndex: number;
    totalItems: number;
    visibleCount: number;
    onPrev: () => void;
    onNext: () => void;
}

const CarouselNav: React.FC<CarouselNavProps> = ({ currentIndex, totalItems, visibleCount, onPrev, onNext }) => {
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex + visibleCount < totalItems;
    
    return (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 1 }}>
            <IconButton 
                onClick={onPrev} 
                disabled={!canGoPrev}
                size="small"
                sx={{ 
                    color: canGoPrev ? 'white' : 'rgba(255,255,255,0.3)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
            >
                <ChevronLeftIcon />
            </IconButton>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 60, textAlign: 'center' }}>
                {currentIndex + 1}-{Math.min(currentIndex + visibleCount, totalItems)} / {totalItems}
            </Typography>
            <IconButton 
                onClick={onNext} 
                disabled={!canGoNext}
                size="small"
                sx={{ 
                    color: canGoNext ? 'white' : 'rgba(255,255,255,0.3)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
            >
                <ChevronRightIcon />
            </IconButton>
        </Stack>
    );
};

function StatsByWeapon(props: {paramObject: any, weapon: string|undefined, setWeapon: React.Dispatch<SetStateAction<undefined|string>>}){
    const ctx = useReferentialContext();
    const [statsByWeapon, setStatsByWeapon] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 2;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'main_weapon', aggregates: ['win__avg'],
            metrics: ['acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByWeapon(r),
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setWeapon(props.weapon === stats.main_weapon ? undefined : stats.main_weapon)} stats={stats} title={<WeaponIcon weapon={stats.main_weapon} width={70} height={35}/>} decoration={<Title>{capitalize(stats.main_weapon)}</Title>} selected={stats.main_weapon === props.weapon}/>
    }

    const visibleItems = statsByWeapon.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Weapon'}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.main_weapon}-${currentIndex + index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByWeapon.length > visibleCount && (
                <CarouselNav 
                    currentIndex={currentIndex}
                    totalItems={statsByWeapon.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByWeapon.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsByEcoType(props: {
    paramObject: any,
    economy: string | undefined,
    setEconomy: React.Dispatch<SetStateAction<undefined | string>>
}) {
    const ctx = useReferentialContext();
    const [statsByEcoType, setStatsByEcoType] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 1;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'round_eco_type_no_bonus', aggregates: ['win__avg'],
            metrics: ['acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByEcoType(r)
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setEconomy(props.economy === stats.round_eco_type_no_bonus ? undefined : stats.round_eco_type_no_bonus)} stats={stats} title={undefined} decoration={<Title>{capitalize(stats.round_eco_type_no_bonus)}</Title>} selected={stats.round_eco_type_no_bonus === props.economy}/>
    }

    const visibleItems = statsByEcoType.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Economy'}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.round_eco_type_no_bonus}-${currentIndex + index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByEcoType.length > visibleCount && (
                <CarouselNav 
                    currentIndex={currentIndex}
                    totalItems={statsByEcoType.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByEcoType.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsBySide(props: {
    paramObject: any,
    side: string | undefined,
    setSide: React.Dispatch<SetStateAction<undefined | string>>
}) {
    const ctx = useReferentialContext();
    const [statsBySide, setStatsBySide] = useState<any[]>([]);
    useEffect(() => {
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'side', aggregates: ['win__avg'],
            metrics: ['acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsBySide(r)
        );
    }, [props.paramObject.watermark]);

    const sideStatsCompetitive = (stats: any) => {
        return <StatsRow onClick={() => props.setSide(props.side === stats.side ? undefined : stats.side)} stats={stats} decoration={<Title>{capitalize(stats.side)}</Title>} title={undefined} selected={stats.side === props.side}/>
    }

    return <NeonCard sx={{width:'100%', height:'100%', display: 'flex', flexDirection: 'column'}}>
        <NeonCardHeader title={'Side'}/>
        <NeonCardContent sx={{
            flex: 1,
            width:'100%', 
            flexDirection:'column',
            alignItems:'flex-start', 
            justifyContent:'flex-start',
            overflow: 'hidden',
            padding: '8px 16px',
        }}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            height:'100%', 
                            paddingTop: '8px',
                            paddingBottom: '8px',
                            paddingRight: '4px',
                            ...scrollable
                        }}
                    >
            {
                statsBySide.map((r, index) => (
                    <React.Fragment key={`${r.side}-${index}`}>
                        {sideStatsCompetitive(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
        </NeonCardContent>
    </NeonCard>
}

function StatsByMap(props: {
    paramObject: any,
    map: string | undefined,
    setMap: React.Dispatch<SetStateAction<undefined | string>>
}) {
    const ctx = useReferentialContext();
    const [statsByMap, setStatsByMap] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 2;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'map', aggregates: ['win__avg'],
            metrics: ['acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByMap(r)
        );
    }, [props.paramObject.watermark]);

    const mapStatsCompetitive = (stats: any) => {
        return <StatsRow onClick={() => props.setMap(props.map === stats.map ? undefined : stats.map)} stats={stats} decoration={<Title>{capitalize(stats.map)}</Title>} title={undefined} selected={stats.map === props.map}/>
    }

    const visibleItems = statsByMap.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Maps'}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.map}-${currentIndex + index}`}>
                        {mapStatsCompetitive(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByMap.length > visibleCount && (
                <CarouselNav 
                    currentIndex={currentIndex}
                    totalItems={statsByMap.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByMap.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsByAgent(props: {
    paramObject: any,
    agent: string | undefined,
    setAgent: React.Dispatch<SetStateAction<undefined | string>>
}) {
    const ctx = useReferentialContext();
    const [statsByAgent, setStatsByAgent] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 2;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.competitiveRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'agent', aggregates: ['win__avg'],
            metrics: ['acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total']}).then(
            r => setStatsByAgent(r)
        );
    }, [props.paramObject.watermark]);

    const agentStatsCompetitive = (stats: any) => {
        return <StatsRow onClick={() => props.setAgent(props.agent === stats.agent ? undefined : stats.agent)} stats={stats} title={<AgentIcon agent_name={stats.agent}/>} decoration={<Title>{stats.agent}</Title>} selected={stats.agent === props.agent}/>
    }

    const visibleItems = statsByAgent.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Agents'}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.agent}-${currentIndex + index}`}>
                        {agentStatsCompetitive(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByAgent.length > visibleCount && (
                <CarouselNav 
                    currentIndex={currentIndex}
                    totalItems={statsByAgent.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByAgent.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}
